import { useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import {
  cancelGbpUploadSession,
  deleteGbpPhoto,
  deleteGbpPhotoSlot,
  fetchGbpPhotos,
  fetchGbpUploadSession,
  patchGbpPhotoSlot,
  syncGbpPhotoSlots,
  uploadGbpPhotoFiles,
} from '../api';

export function useGbpPhotos(filters?: { category?: string }) {
  return useGrowthQuery({
    queryKey: growthKeys.gbp.photos(filters ?? {}),
    queryFn: () => fetchGbpPhotos(filters),
  });
}

export function useGbpUploadSession(id: string, enabled = false) {
  return useGrowthQuery({
    queryKey: growthKeys.gbp.uploadSession(id),
    queryFn: () => fetchGbpUploadSession(id),
    enabled: enabled && Boolean(id),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;
      return data.isProcessing ? 2000 : false;
    },
  });
}

export function useGbpPhotoMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidatePhotos = () =>
    void queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'gbp', 'photos'] });

  const uploadPhotos = useMutation({
    mutationFn: ({
      files,
      promptId,
    }: {
      files: File[];
      promptId?: string;
    }) =>
      uploadGbpPhotoFiles(files, promptId ? { prompt_id: promptId } : undefined),
    retry: false,
    onError: (e) => notify.error(e),
  });

  const removePhoto = useMutation({
    mutationFn: deleteGbpPhoto,
    onSuccess: () => {
      notify.success('Photo removed');
      invalidatePhotos();
    },
    onError: (e) => notify.error(e),
  });

  const syncSlots = useMutation({
    mutationFn: ({ sessionId, slotIds }: { sessionId: string; slotIds: number[] }) =>
      syncGbpPhotoSlots(sessionId, slotIds),
    onSuccess: () => {
      notify.success('Photos synced to Google');
      invalidatePhotos();
    },
    onError: (e) => notify.error(e),
  });

  const patchSlot = useMutation({
    mutationFn: ({
      slotId,
      body,
    }: {
      slotId: number;
      body: { classified_category?: string; description?: string };
    }) => patchGbpPhotoSlot(slotId, body),
    onError: (e) => notify.error(e),
  });

  const removeSlot = useMutation({
    mutationFn: deleteGbpPhotoSlot,
    onSuccess: () => notify.success('Photo removed from batch'),
    onError: (e) => notify.error(e),
  });

  const cancelSession = useMutation({
    mutationFn: cancelGbpUploadSession,
    onSuccess: () => notify.success('Upload cancelled'),
    onError: (e) => notify.error(e),
  });

  return {
    uploadPhotos,
    removePhoto,
    syncSlots,
    patchSlot,
    removeSlot,
    cancelSession,
  };
}
